import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Crown, Home, Settings, Users, LogOut, Menu, X, ChevronDown, MessageCircle, Group as UserGroup, ShoppingBag, Sparkles } from 'lucide-react';
import { useAuth } from '@/lib/auth';
import { cn } from '@/lib/utils';
import { MessagesPopup } from '../messaging/MessagesPopup';
import { NotificationBell } from '../notifications/NotificationBell';
import { ThemeToggle } from '../ui/ThemeToggle';
import { LanguageSelector } from '../ui/LanguageSelector';
import { supabase } from '@/lib/supabase';
import { Footer } from './Footer';
import { SellerOnboardingDialog } from '../marketplace/seller/SellerOnboardingDialog';

interface MainLayoutProps {
  children: React.ReactNode;
}

export function MainLayout({ children }: MainLayoutProps) {
  const { user, signOut } = useAuth();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [showMessages, setShowMessages] = useState(false);
  const [selectedConversationId, setSelectedConversationId] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isSeller, setIsSeller] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [showSellerOnboarding, setShowSellerOnboarding] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (user) {
      loadProfile();
    }
  }, [user]);

  const loadProfile = async () => {
    try {
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('role, avatar_url, is_seller')
        .eq('id', user?.id)
        .single();

      if (profileError) throw profileError;

      setIsAdmin(profile?.role === 'admin');
      setIsSeller(profile?.is_seller || false);
      if (profile?.avatar_url) {
        setAvatarUrl(profile.avatar_url);
      }
    } catch (error) {
      console.error('Error loading profile:', error);
    }
  };

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setIsUserMenuOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleOpenMessage = (conversationId: string) => {
    setSelectedConversationId(conversationId);
    setShowMessages(true);
  };

  const location = useLocation();

  const navigation = [
    { name: 'Feed', href: '/', icon: Home },
    { name: 'Discover', href: '/discover', icon: Sparkles },
    { name: 'Groups', href: '/groups', icon: UserGroup }
  ];

  const userNavigation = [
    { name: 'Profile', href: `/profile/${user?.id}`, icon: Users },
    { name: 'Settings', href: '/settings', icon: Settings },
    ...(isAdmin ? [{ name: 'Admin', href: '/admin', icon: Crown }] : [])
  ];

  const marketplaceNavigation = [
    { name: 'Browse Products', href: '/marketplace' },
    { name: 'Shopping Cart', href: '/marketplace/cart' },
    ...(isSeller ? [
      { name: 'Seller Dashboard', href: '/marketplace/seller' }
    ] : [
      { 
        name: 'Become a Seller', 
        onClick: () => setShowSellerOnboarding(true) 
      }
    ])
  ];

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-dark flex flex-col">
      <header className="bg-white dark:bg-dark-paper shadow-sm fixed top-0 left-0 right-0 z-50">
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-between items-center h-16 px-4 sm:px-6 lg:px-8">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3 group">
              <div className="relative">
                <div className={cn(
                  "absolute inset-0 bg-royal-purple/20 rounded-full blur-lg group-hover:blur-xl transition-all",
                  "dark:bg-royal-purple/30 dark:group-hover:bg-royal-purple/40"
                )} />
                <Crown className={cn(
                  "w-8 h-8 text-royal-purple relative transform group-hover:scale-110 transition-transform",
                  "dark:drop-shadow-[0_0_3px_rgba(120,81,169,0.5)]"
                )} />
              </div>
              <span className={cn(
                "text-xl font-bold text-royal-purple hidden sm:block",
                "dark:drop-shadow-[0_0_2px_rgba(120,81,169,0.3)]"
              )}>
                RoyaltySuite
              </span>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-4">
              {navigation.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.href;
                
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={cn(
                      'flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200',
                      isActive
                        ? 'bg-royal-purple text-white shadow-lg shadow-royal-purple/25 hover:shadow-royal-purple/40'
                        : 'text-gray-600 dark:text-dark-primary hover:bg-royal-purple/10 hover:text-royal-purple'
                    )}
                  >
                    <Icon className={cn('w-5 h-5', isActive ? 'animate-pulse' : '')} />
                    <span>{item.name}</span>
                  </Link>
                );
              })}

              {/* Messages button */}
              <button
                onClick={() => {
                  setShowMessages(true);
                  setSelectedConversationId(null);
                }}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg text-gray-600 dark:text-dark-primary hover:bg-royal-purple/10 hover:text-royal-purple transition-all duration-200"
              >
                <MessageCircle className="w-5 h-5" />
                <span>Messages</span>
              </button>

              {/* Notifications */}
              <NotificationBell onOpenMessage={handleOpenMessage} />

              {/* Marketplace */}
              <div className="relative group">
                <Link
                  to="/marketplace"
                  className={cn(
                    'flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200',
                    location.pathname.startsWith('/marketplace')
                      ? 'bg-royal-purple text-white shadow-lg shadow-royal-purple/25 hover:shadow-royal-purple/40'
                      : 'text-gray-600 dark:text-dark-primary hover:bg-royal-purple/10 hover:text-royal-purple'
                  )}
                >
                  <ShoppingBag className="w-5 h-5" />
                  <span>Marketplace</span>
                </Link>

                <div className="absolute left-0 mt-1 w-48 bg-white dark:bg-dark-paper rounded-lg shadow-lg border border-gray-100 dark:border-dark-border py-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                  {marketplaceNavigation.map((item) => (
                    <div key={item.name}>
                      {item.href ? (
                        <Link
                          to={item.href}
                          className="block px-4 py-2 text-sm text-gray-700 dark:text-dark-primary hover:bg-gray-100 dark:hover:bg-dark-elevated"
                        >
                          {item.name}
                        </Link>
                      ) : (
                        <button
                          onClick={item.onClick}
                          className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-dark-primary hover:bg-gray-100 dark:hover:bg-dark-elevated"
                        >
                          {item.name}
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </nav>

            {/* User menu */}
            <div className="flex items-center gap-4">
              {/* Theme and Language */}
              <div className="hidden md:flex items-center gap-2">
                <ThemeToggle />
                <LanguageSelector />
              </div>

              {/* User dropdown */}
              <div className="relative" ref={userMenuRef}>
                <button
                  onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                  className="hidden sm:flex items-center gap-3 bg-gray-50 dark:bg-dark-elevated px-4 py-2 rounded-lg hover:bg-gray-100 dark:hover:bg-dark-paper transition-colors group"
                >
                  <img
                    src={avatarUrl || `https://api.dicebear.com/7.x/initials/svg?seed=${user?.email}`}
                    alt="Avatar"
                    className="w-8 h-8 rounded-full ring-2 ring-royal-purple/20"
                  />
                  <span className="text-sm font-medium text-gray-700 dark:text-dark-primary">
                    {user?.email}
                  </span>
                  <ChevronDown className={cn(
                    'w-4 h-4 text-gray-500 dark:text-dark-secondary transition-transform duration-200',
                    isUserMenuOpen ? 'rotate-180' : ''
                  )} />
                </button>

                {/* Dropdown menu */}
                {isUserMenuOpen && (
                  <div className="absolute right-0 mt-2 w-56 bg-white dark:bg-dark-paper rounded-lg shadow-lg py-2 border border-gray-100 dark:border-dark-border animate-fade-in">
                    {userNavigation.map((item) => {
                      const Icon = item.icon;
                      const isActive = location.pathname === item.href;
                      return (
                        <Link
                          key={item.name}
                          to={item.href}
                          onClick={() => setIsUserMenuOpen(false)}
                          className={cn(
                            'flex items-center gap-3 px-4 py-2 text-sm transition-colors',
                            isActive
                              ? 'text-royal-purple bg-royal-purple/5 font-medium'
                              : 'text-gray-600 dark:text-dark-primary hover:bg-gray-50 dark:hover:bg-dark-elevated hover:text-royal-purple'
                          )}
                        >
                          <Icon className="w-4 h-4" />
                          {item.name}
                        </Link>
                      );
                    })}
                    <hr className="my-2 border-gray-100 dark:border-dark-border" />
                    <button
                      onClick={() => {
                        setIsUserMenuOpen(false);
                        signOut();
                      }}
                      className="w-full flex items-center gap-3 px-4 py-2 text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/10 transition-colors"
                    >
                      <LogOut className="w-4 h-4" />
                      Sign out
                    </button>
                  </div>
                )}
              </div>

              {/* Mobile menu button */}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="md:hidden p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-dark-elevated transition-colors"
              >
                {isMobileMenuOpen ? (
                  <X className="w-6 h-6 text-gray-700 dark:text-dark-primary" />
                ) : (
                  <Menu className="w-6 h-6 text-gray-700 dark:text-dark-primary" />
                )}
              </button>
            </div>
          </div>

          {/* Mobile Navigation */}
          {isMobileMenuOpen && (
            <nav className="md:hidden border-t border-gray-200 dark:border-dark-border bg-white dark:bg-dark-paper">
              <div className="px-2 pt-2 pb-3 space-y-1">
                {[...navigation, ...userNavigation].map((item) => {
                  const Icon = item.icon;
                  const isActive = location.pathname === item.href;
                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      onClick={() => setIsMobileMenuOpen(false)}
                      className={cn(
                        'flex items-center gap-3 px-3 py-2 rounded-lg text-base font-medium transition-all duration-200',
                        isActive
                          ? 'bg-royal-purple text-white shadow-lg shadow-royal-purple/25'
                          : 'text-gray-600 dark:text-dark-primary hover:bg-royal-purple/10 hover:text-royal-purple'
                      )}
                    >
                      <Icon className="w-5 h-5" />
                      <span>{item.name}</span>
                    </Link>
                  );
                })}

                {/* Mobile marketplace menu */}
                {marketplaceNavigation.map((item) => (
                  <div key={item.name}>
                    {item.href ? (
                      <Link
                        to={item.href}
                        onClick={() => setIsMobileMenuOpen(false)}
                        className="flex items-center gap-3 px-3 py-2 rounded-lg text-base font-medium text-gray-600 dark:text-dark-primary hover:bg-royal-purple/10 hover:text-royal-purple transition-all duration-200"
                      >
                        <span>{item.name}</span>
                      </Link>
                    ) : (
                      <button
                        onClick={() => {
                          setIsMobileMenuOpen(false);
                          item.onClick?.();
                        }}
                        className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-base font-medium text-gray-600 dark:text-dark-primary hover:bg-royal-purple/10 hover:text-royal-purple transition-all duration-200"
                      >
                        <span>{item.name}</span>
                      </button>
                    )}
                  </div>
                ))}

                {/* Mobile messages button */}
                <button
                  onClick={() => {
                    setIsMobileMenuOpen(false);
                    setShowMessages(true);
                    setSelectedConversationId(null);
                  }}
                  className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-base font-medium text-gray-600 dark:text-dark-primary hover:bg-royal-purple/10 hover:text-royal-purple transition-all duration-200"
                >
                  <MessageCircle className="w-5 h-5" />
                  <span>Messages</span>
                </button>

                {/* Mobile theme and language */}
                <div className="flex items-center gap-2 px-3 py-2">
                  <ThemeToggle />
                  <LanguageSelector />
                </div>

                <button
                  onClick={() => {
                    setIsMobileMenuOpen(false);
                    signOut();
                  }}
                  className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-base font-medium text-red-600 hover:bg-red-50 dark:hover:bg-red-900/10 transition-colors"
                >
                  <LogOut className="w-5 h-5" />
                  <span>Sign out</span>
                </button>
              </div>
            </nav>
          )}
        </div>
      </header>

      {/* Main content */}
      <main className="pt-24 pb-12 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto flex-1">
        {children}
      </main>

      {/* Add Footer */}
      <Footer />

      {/* Messages popup */}
      {showMessages && (
        <MessagesPopup 
          onClose={() => {
            setShowMessages(false);
            setSelectedConversationId(null);
          }}
          initialConversationId={selectedConversationId}
        />
      )}

      {/* Seller Onboarding Dialog */}
      {showSellerOnboarding && (
        <SellerOnboardingDialog onClose={() => setShowSellerOnboarding(false)} />
      )}
    </div>
  );
}